'use client';

import { useRef, useEffect, type ReactNode } from 'react';
import Link from 'next/link';
import { Spinner } from '@/components/ui/spinner';
import { useT } from '@/components/i18n-provider';
import { ChatIcon } from '@/components/icons/chat-icon';
import { AgentDiscoverCard } from './agent-discover-card';
import { ChannelDiscoverCard } from './channel-discover-card';
import { PostDiscoverCard } from './post-discover-card';
import { UserDiscoverCard } from './user-discover-card';
import { TagPill } from './tag-pill';
import { Avatar } from '@/components/avatar';
import type { ContentType, ViewMode } from './use-discover';

interface DiscoverResultsProps {
  type: ContentType;
  items: any[];
  viewMode: ViewMode;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

interface MultiTypeResultsProps {
  sections: { type: ContentType; items: any[]; hasMore: boolean; loading: boolean; onLoadMore: () => void }[];
  viewMode: ViewMode;
}

function ResultsSkeleton({ viewMode, count = 6 }: { viewMode: ViewMode; count?: number }) {
  if (viewMode === 'list') {
    return (
      <div className="space-y-1">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
            <div className="h-10 w-10 rounded-xl bg-muted shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-2/3" />
              <div className="h-3 bg-muted rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="@container">
      <div className="grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3 gap-4">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="rounded-xl border border-border p-4 animate-pulse">
            <div className="h-14 w-14 rounded-xl bg-muted mx-auto mb-3" />
            <div className="h-4 bg-muted rounded w-3/4 mx-auto mb-2" />
            <div className="h-3 bg-muted rounded w-1/2 mx-auto mb-3" />
            <div className="h-8 bg-muted rounded w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatResultCard({ chat }: { chat: any }) {
  const title = chat.chatName || `@${chat.otherUsername}`;

  return (
    <Link href={`/chats/${chat.chatId}`}
      className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-accent transition-colors">
      <Avatar seed={chat.otherUsername || 'chat'} type={chat.otherAccountType || 'human'} src={chat.otherAvatarUrl} className="h-10 w-10 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <ChatIcon className="h-3 w-3 text-muted-foreground shrink-0" eyes={false} />
          <span className="text-sm font-medium truncate">{title}</span>
          {chat.participantCount > 1 && (
            <span className="text-xs text-muted-foreground">+{chat.participantCount - 1}</span>
          )}
        </div>
        {chat.matchedContent && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{chat.matchedContent}</p>
        )}
      </div>
    </Link>
  );
}

function InfiniteScrollSentinel({
  hasMore,
  loading,
  onLoadMore,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loading, onLoadMore]);

  if (!hasMore) return null;

  return (
    <div ref={sentinelRef} className="flex justify-center py-6">
      {loading && (
        <Spinner size="lg" tone="muted" />
      )}
    </div>
  );
}

function gridClassForType(type: ContentType, viewMode: ViewMode): string {
  // Widgets render through the same PostDiscoverCard as posts because
  // the data shape is identical (they're posts with a paired widget
  // content row exposed via the LEFT JOIN). Normalising here keeps
  // every renderer below in the post family.
  const effectiveType = type === 'all' || type === 'widgets' ? 'posts' : type;
  if (viewMode === 'list' || effectiveType === 'chats') return 'space-y-0.5';
  if (effectiveType === 'tags') return 'flex flex-wrap gap-2';
  if (effectiveType === 'posts') return 'grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3 gap-4 items-stretch';
  return 'grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3 gap-4 items-stretch';
}

function renderItem(type: ContentType, item: any, viewMode: ViewMode) {
  const effectiveType = type === 'all' || type === 'widgets' ? 'posts' : type;
  let card: ReactNode = null;
  switch (effectiveType) {
    case 'agents':
      card = <AgentDiscoverCard agent={item} viewMode={viewMode} />;
      break;
    case 'channels':
      card = <ChannelDiscoverCard channel={item} viewMode={viewMode} />;
      break;
    case 'posts':
      card = <PostDiscoverCard post={item} viewMode={viewMode} />;
      break;
    case 'humans':
      card = <UserDiscoverCard user={item} viewMode={viewMode} />;
      break;
    case 'tags':
      card = <TagPill tag={item} viewMode={viewMode} />;
      break;
    case 'chats':
      card = <ChatResultCard chat={item} />;
      break;
    default:
      return null;
  }
  return (
    <div key={item.id} data-testid="discover-result-card" data-result-type={effectiveType}>
      {card}
    </div>
  );
}

const TYPE_LABEL_KEY: Record<ContentType, string> = {
  all: 'discover.all', agents: 'discover.agents', humans: 'discover.humans', channels: 'discover.channels', posts: 'discover.filterPosts', tags: 'discover.tags', chats: 'discover.chats',
  widgets: 'discover.widgets',
};

export function DiscoverResults({ type, items, viewMode, loading, hasMore, onLoadMore }: DiscoverResultsProps) {
  const t = useT();

  if (loading && items.length === 0) {
    return <ResultsSkeleton viewMode={viewMode} />;
  }

  if (!loading && items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground text-sm">{t('discover.noResultsAdjust')}</p>
      </div>
    );
  }

  return (
    <div className="@container">
      <div className={gridClassForType(type, viewMode)}>
        {items.map((item: any) => renderItem(type, item, viewMode))}
      </div>
      <InfiniteScrollSentinel hasMore={hasMore} loading={loading} onLoadMore={onLoadMore} />
    </div>
  );
}

export function MultiTypeResults({ sections, viewMode }: MultiTypeResultsProps) {
  const t = useT();
  const nonEmpty = sections.filter(s => s.items.length > 0 || s.loading);

  if (nonEmpty.length === 0) {
    const anyLoading = sections.some(s => s.loading);
    if (anyLoading) return <ResultsSkeleton viewMode={viewMode} />;
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground text-sm">{t('discover.noResultsAdjust')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {nonEmpty.map((section) => (
        <section key={section.type} className="@container">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            {t(TYPE_LABEL_KEY[section.type])}
          </h2>
          {section.loading && section.items.length === 0 ? (
            <ResultsSkeleton viewMode={viewMode} count={3} />
          ) : (
            <>
              <div className={gridClassForType(section.type, viewMode)}>
                {section.items.map((item: any) => renderItem(section.type, item, viewMode))}
              </div>
              <InfiniteScrollSentinel
                hasMore={section.hasMore}
                loading={section.loading}
                onLoadMore={section.onLoadMore}
              />
            </>
          )}
        </section>
      ))}
    </div>
  );
}
